import React from 'react';
import {useTranslation} from 'react-i18next';
import {OrderInfo, Product} from '../../../../../types/TypesUserOrders';
import {
	useGetMyTeamClientOrderQuery,
} from '../../../../../redux/userClientsApi';
import {monthNow} from '../../../../../assets/utils/dateUtils';
import LightGreenButton from '../../../../../Components/Buttons/LightGreenButton';
import GridTableHead from '../../../../../Components/GridTableHead/GridTableHead';
import styles from './OrdersTable.module.css';

interface OrdersTableProps {
	clientId: number;
	selectedMonth?: string;
	showTable: boolean;
}

const statusClass = (status: string) => {
	switch (status) {
		case 'approved':
		case 'paid':
			return styles.statusGreen;
		case 'rejected':
			return styles.statusRed;
		default:
			return styles.statusYellow;
	}
};

const ProductRow: React.FC<{products: Product[]}> = ({products}) => {
	const {t} = useTranslation();
	return (
		<table className={styles.productsTable}>
			<thead>
				<tr>
					<th>{t('Фото')}</th>
					<th>{t('Назва')}</th>
					<th>{t('Кількість')}</th>
				</tr>
			</thead>
			<tbody>
				{products.map((p) => (
					<tr key={p.href + p.name}>
						<td className={styles.productImageCell}>
							{p.href ? (
								<img className={styles.productImg} src={p.href} alt={p.name} />
							) : (
								<span className={styles.muted}>—</span>
							)}
						</td>
						<td className={styles.productName}>{p.name}</td>
						<td className={styles.productCount}>{p.count}</td>
					</tr>
				))}
			</tbody>
		</table>
	);
};

const OrdersTable: React.FC<OrdersTableProps> = ({
	clientId,
	selectedMonth,
	showTable,
}) => {
	const {t} = useTranslation();
	const [expandedKey, setExpandedKey] = React.useState<string | null>(null);
	const [visibleCount, setVisibleCount] = React.useState(20);

	// Запрос данных заказов
	const {data: ordersData, isLoading} = useGetMyTeamClientOrderQuery(
		{
			month: selectedMonth || monthNow(),
			client_id: clientId,
		},
		{
			skip: !showTable,
		}
	);

	// Мапа статусів з перекладами
	const statusMap = {
		approved: t('Підтверджено'),
		paid: t('Оплачено'),
		rejected: t('Відхилено'),
		pending: t('Очікування'),
		new: t('Новий'),
	} as const;

	type StatusType = keyof typeof statusMap;
	const getStatusText = (status: string) =>
		statusMap[status as StatusType] || status;

	// Мапа типів замовлень з перекладами
	const typeIconInfo = (type?: string) => {
		switch (type) {
			case 'order':
				return {icon: '📄', title: t('звичайне замовлення')};
			case 'deferred':
				return {icon: '❌📄', title: t('замовлення з відкладеним оформленням')};
			case 'activity':
				return {icon: '➡️', title: t('відкладені оформлення')};
			case 'store':
				return {icon: '🏬', title: t('складські оформлення')};
			case 'referral':
				return {icon: '🔗', title: t('реферальні замовлення')};
			default:
				return {icon: '', title: t('невідомо')};
		}
	};

	// Если не показана таблица, не рендерим ничего
	if (!showTable) {
		return null;
	}

	// Если загружается, показываем спиннер
	if (isLoading) {
		return (
			<div className={styles.spinner}>
				{t('Завантаження...')}
			</div>
		);
	}

	// Если данных нет
	if (!ordersData || !(ordersData as any)?.orders || (ordersData as any).orders.length === 0) {
		return null; // Не показываем ничего, если данных нет
	}

	const orders = (ordersData as any).orders.slice(0, visibleCount);

	// Настройка колонок для заказов
	const columns = [' ', t('Дата'), t('Бали'), t('Сума'), t('Вид'), t('Статус')];
	const gridTemplate = '50px minmax(100px, 120px) 80px 100px 70px 100px';
	const labels = {
		toggle: '',
		date: t('Дата'),
		points: t('Бали'),
		money: t('Сума'),
		type: t('Вид'),
		status: t('Статус'),
	};

	return (
		<div className={styles.wrapper}>
			<div className={styles.scroll}>
				<div className={styles.tableHeadWrapper}>
					<GridTableHead
						columns={columns}
						gridTemplateColumns={gridTemplate}
						customClassName={styles.gridTableHead}
					/>
				</div>
				{orders.map((order: OrderInfo) => {
					const canExpand = order.money != null && order.products != null;
					const rowKey = order.create_date;
					const isOpen = expandedKey === rowKey;
					const tip = `${t('Дата створення')} ${order.create_date}`;
					const typeInfo = typeIconInfo((order as any).type);

					return (
						<React.Fragment key={order.id ?? rowKey}>
							<div
								className={styles.row}
								style={{gridTemplateColumns: gridTemplate}}
							>
								<div
									className={`${styles.cell} ${styles.colToggle}`}
									data-label={labels.toggle}
								>
									{canExpand ? (
										<button
											className={styles.toggleBtn}
											aria-label={isOpen ? t('Згорнути') : t('Розгорнути')}
											onClick={() => setExpandedKey(isOpen ? null : rowKey)}
											title={isOpen ? t('Згорнути') : t('Показати товари')}
										>
											{isOpen ? '▾' : '▸'}
										</button>
									) : (
										<span className={styles.emptyToggle}></span>
									)}
								</div>
								<div
									className={`${styles.cell} ${styles.colDate}`}
									data-label={labels.date}
									title={tip}
								>
									{order.order_date || (order as any).act_date}
								</div>
								<div
									className={`${styles.cell} ${styles.colPoints}`}
									data-label={labels.points}
								>
									{order.points}
								</div>
								<div
									className={`${styles.cell} ${styles.colMoney}`}
									data-label={labels.money}
								>
									{order.money == null ? (
										<span className={styles.muted}>—</span>
									) : (
										order.money
									)}
								</div>
								<div
									className={`${styles.cell} ${styles.colType}`}
									data-label={labels.type}
								>
									<div className={styles.icons} title={typeInfo.title}>
										<span>{typeInfo.icon}</span>
									</div>
								</div>
								<div
									className={`${styles.cell} ${styles.colStatus}`}
									data-label={labels.status}
								>
									<span
										className={`${styles.status} ${statusClass(order.status)}`}
									>
										{getStatusText(order.status)}
									</span>
								</div>
							</div>
							{isOpen && order.products && (
								<div className={styles.productsRow}>
									<ProductRow products={order.products} />
								</div>
							)}
						</React.Fragment>
					);
				})}
			</div>

			{(ordersData as any).orders.length > visibleCount && (
				<div className={styles.actions}>
					<LightGreenButton
						text={t('Показати ще')}
						onClick={() => setVisibleCount((v) => v + 20)}
						type="button"
					/>
				</div>
			)}
		</div>
	);
};

export default OrdersTable;
